import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from './supabase';

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Outfit:wght@300;400;500;600;700&display=swap');

* { box-sizing: border-box; margin: 0; padding: 0; text-decoration: none; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes popIn {
  0%   { opacity: 0; transform: scale(0.88); }
  70%  { transform: scale(1.03); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes confettiFall {
  0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}
@keyframes slideRight {
  from { opacity: 0; transform: translateX(30px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes slideLeft {
  from { opacity: 0; transform: translateX(-30px); }
  to   { opacity: 1; transform: translateX(0); }
}

.fade-up    { animation: fadeUp .45s ease both; }
.fade-in    { animation: fadeIn .3s ease both; }
.pop-in     { animation: popIn .4s cubic-bezier(.34,1.56,.64,1) both; }
.slide-right { animation: slideRight .35s ease both; }
.slide-left  { animation: slideLeft .35s ease both; }

.mode-card {
  background: #111;
  border-radius: 20px;
  padding: 32px;
  cursor: pointer;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  border: 1px solid #1e1e1e;
  position: relative;
  overflow: hidden;
}
.mode-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 48px #00000066;
}

.answer-btn {
  width: 100%;
  background: #0f0f0f;
  border: 1.5px solid #2a2a2a;
  border-radius: 14px;
  padding: 16px 20px;
  color: #c8c8c8;
  font-family: 'Outfit', sans-serif;
  font-size: 15px;
  cursor: pointer;
  text-align: left;
  transition: all .15s ease;
  display: flex;
  align-items: center;
  gap: 14px;
  line-height: 1.4;
}
.answer-btn:hover:not(:disabled) {
  border-color: #444;
  background: #161616;
  color: #F0EDE6;
  transform: translateX(3px);
}
.answer-btn.correct { background: #0d2418; border-color: #27AE60; color: #4ade80; }
.answer-btn.wrong   { background: #2a0d0d; border-color: #C0392B; color: #f87171; }

.flash-card-inner {
  position: relative;
  width: 100%;
  height: 320px;
  transform-style: preserve-3d;
  transition: transform .6s cubic-bezier(.4,0,.2,1);
  cursor: pointer;
}
.flash-card-inner.flipped { transform: rotateY(180deg); }
.flash-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}
.flash-back { transform: rotateY(180deg); }

.chapter-pill {
  background: transparent;
  border: 1.5px solid #2a2a2a;
  border-radius: 100px;
  padding: 6px 18px;
  font-size: 12px;
  font-family: 'Outfit', sans-serif;
  color: #555;
  cursor: pointer;
  transition: all .15s;
  font-weight: 500;
}
.chapter-pill:hover { border-color: #444; color: #888; }
.chapter-pill.active { font-weight: 700; }

.progress-track {
  width: 100%;
  height: 3px;
  background: #1a1a1a;
  border-radius: 100px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 100px;
  transition: width .6s cubic-bezier(.4,0,.2,1);
}

.confetti-piece {
  position: fixed;
  animation: confettiFall linear forwards;
  z-index: 9999;
  pointer-events: none;
}

.concept-card {
  background: #0a0a0a;
  border: 1px solid #1a1a1a;
  border-radius: 12px;
  padding: 18px 20px;
  transition: border-color .2s;
}
.concept-card:hover { border-color: #2a2a2a; }

.dot-nav { display: flex; gap: 6px; justify-content: center; align-items: center; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: #2a2a2a; transition: all .2s; cursor: pointer; }
.dot.active { width: 20px; border-radius: 3px; }

@keyframes screenIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.screen-enter { animation: screenIn .3s ease both; }

.pill-scroll {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding-bottom: 4px;
}
.pill-scroll::-webkit-scrollbar { display: none; }
`;

const POPULAR_ORDER = [
  'atomic-habits','deep-work','psychology-money','cant-hurt-me',
  'extreme-ownership','start-with-why','how-to-win-friends',
  '48-laws-power','rich-dad-poor-dad','7-habits'
];
const FREE_BOOK = 'extreme-ownership';
const FREE_CHAPTERS = 2; // guests can access this many chapters per book

function Confetti({ active }) {
  const pieces = useRef([]);
  if (!active) return null;
  const colors = ['#C8B96E','#27AE60','#3498DB','#E67E22','#9B59B6','#E74C3C','#F0EDE6'];
  if (!pieces.current.length) {
    pieces.current = Array.from({ length: 80 }, (_, i) => ({
      id: i, color: colors[i % colors.length],
      left: Math.random() * 100, delay: Math.random() * 2,
      duration: 2 + Math.random() * 2, size: 6 + Math.random() * 6,
    }));
  }
  return (
    <>
      {pieces.current.map(p => (
        <div key={p.id} className="confetti-piece" style={{
          left:`${p.left}%`, top:'-10px', background:p.color,
          width:p.size, height:p.size, borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          animationDelay:`${p.delay}s`, animationDuration:`${p.duration}s`,
        }} />
      ))}
    </>
  );
}

function SummaryModule({ book, chapters, takeaways, accent, isMobile, onComplete, userProgress, saveProgress, guestLimited, onSignUp }) {
  const [chapterIndex, setChapterIndex] = useState(-1);
  const [completedChapters, setCompletedChapters] = useState(() => {
    const prog = userProgress?.find(p => p.book_id === book.id);
    return prog?.completed_chapters || [];
  });
  const [direction, setDirection] = useState('right');

  useEffect(() => {
    const prog = userProgress?.find(p => p.book_id === book.id);
    if (prog?.completed_chapters?.length > 0) {
      setCompletedChapters(prog.completed_chapters);
    }
  }, [userProgress, book.id]);

  const progress = chapters.length > 0 ? Math.round((completedChapters.length / chapters.length) * 100) : 0;

  const goTo = (idx) => {
    setDirection(idx > chapterIndex ? 'right' : 'left');
    setChapterIndex(idx);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  const markComplete = () => {
    const c = chapters[chapterIndex];
    if (!c) return;
    const updated = completedChapters.includes(c.id) ? completedChapters : [...completedChapters, c.id];
    setCompletedChapters(updated);
    saveProgress(book.id, { completed_chapters: updated, summary_viewed: true });
    if (chapterIndex < chapters.length - 1) {
      goTo(chapterIndex + 1);
    } else {
      onComplete && onComplete();
    }
  };

  const serif = { fontFamily:"'Cormorant Garamond', serif" };
  const chapter = chapters[chapterIndex];
  const chapterTakeaways = chapter ? takeaways.filter(t => t.chapter_id === chapter.id) : takeaways;
  const animClass = direction === 'right' ? 'slide-right' : 'slide-left';

  return (
    <div>
      <div style={{ marginBottom:40 }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
          <span style={{ fontSize:11, color:'#444', fontWeight:600, letterSpacing:1 }}>{completedChapters.length}/{chapters.length} CHAPTERS COMPLETE</span>
          <span style={{ fontSize:11, color: progress===100 ? '#4ade80' : accent, fontWeight:700 }}>{progress}%</span>
        </div>
        <div className="progress-track" style={{ height:4, marginBottom:20 }}>
          <div className="progress-fill" style={{ width:`${progress}%`, background: progress===100 ? '#27AE60' : accent }} />
        </div>
        <div className="pill-scroll">
          <button onClick={() => goTo(-1)} className={`chapter-pill${chapterIndex===-1?' active':''}`}
            style={{ borderColor:chapterIndex===-1?accent:undefined, color:chapterIndex===-1?accent:undefined, flexShrink:0 }}>Overview</button>
          {chapters.map((c, i) => {
            const done = completedChapters.includes(c.id);
            return (
              <button key={c.id} onClick={() => goTo(i)} className={`chapter-pill${chapterIndex===i?' active':''}`}
                style={{ borderColor:chapterIndex===i?accent:done?'#27AE6044':undefined, color:chapterIndex===i?accent:done?'#4ade80':undefined, flexShrink:0 }}>
                {done ? '✓ ' : ''}Ch {c.number}
              </button>
            );
          })}
        </div>
      </div>

      {chapterIndex === -1 && (
        <div key="overview" className={animClass}>
          <div style={{ background:`linear-gradient(135deg,${book.palette[0]}33,#0a0a0a)`, border:`1px solid ${accent}18`, borderRadius:24, padding: isMobile?'28px 24px':'44px 48px', marginBottom:36 }}>
            <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginBottom:20 }}>BOOK OVERVIEW</div>
            <h2 style={{ ...serif, fontSize: isMobile?28:38, fontWeight:600, lineHeight:1.15, marginBottom:16 }}>{book.title}</h2>
            <div style={{ fontSize:14, color:'#555', marginBottom:28 }}>{book.author} · {book.year}</div>
            <p style={{ color:'#777', fontSize: isMobile?15:16, lineHeight:1.9 }}>{book.description}</p>
          </div>
          <div style={{ marginBottom:36 }}>
            <div style={{ fontSize:10, color:'#444', fontWeight:700, letterSpacing:2, marginBottom:20 }}>WHAT YOU'LL LEARN — {chapters.length} CHAPTERS</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {chapters.map((c, i) => {
                const done = completedChapters.includes(c.id);
                return (
                  <div key={c.id} onClick={() => goTo(i)}
                    style={{ display:'flex', alignItems:'center', gap:16, background:'#0a0a0a', border:`1px solid ${done?'#27AE6022':'#151515'}`, borderRadius:12, padding:'14px 18px', cursor:'pointer', transition:'border-color .2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = done?'#27AE6044':accent+'33'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = done?'#27AE6022':'#151515'}>
                    <div style={{ ...serif, fontSize:20, color:done?'#4ade80':accent, fontWeight:600, minWidth:32, opacity:done?1:0.5 }}>{String(i+1).padStart(2,'0')}</div>
                    <div style={{ flex:1, fontWeight:600, fontSize:14, color:'#e8e8e8' }}>{c.title}</div>
                    <span style={{ fontSize:12, color:done?'#4ade80':'#333' }}>{done?'✓':'→'}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {takeaways.length > 0 && (
            <div style={{ marginBottom:36 }}>
              <div style={{ fontSize:10, color:'#444', fontWeight:700, letterSpacing:2, marginBottom:20 }}>KEY IDEAS FROM THIS BOOK</div>
              {takeaways.map((t, i) => (
                <div key={t.id} style={{ display:'flex', gap:18, padding:'20px 0', borderBottom:'1px solid #0f0f0f' }}>
                  <div style={{ ...serif, fontSize:26, color:accent, fontWeight:600, minWidth:36, lineHeight:1, opacity:0.6, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:'#e0e0e0', marginBottom:6 }}>{t.title}</div>
                    <div style={{ color:'#555', fontSize:13, lineHeight:1.7 }}>{t.idea}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <button style={{ background:accent, color:'#080808', border:'none', borderRadius:12, padding:'14px 32px', fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:15, cursor:'pointer', width:'100%' }}
            onClick={() => goTo(0)}>Start Chapter 1 →</button>
        </div>
      )}

      {chapterIndex >= 0 && chapter && (
        <div key={chapter.id} className={animClass}>
          {/* Guest gate — show signup prompt instead of chapter content for ch 3+ */}
          {guestLimited && chapter.number > FREE_CHAPTERS ? (
            <div style={{ textAlign:'center', padding: isMobile?'48px 20px':'72px 40px' }}>
              <div style={{ fontSize:56, marginBottom:20 }}>📖</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize: isMobile?28:36, fontWeight:600, marginBottom:12, lineHeight:1.2 }}>
                You've read {FREE_CHAPTERS} free chapters
              </div>
              <div style={{ fontSize:15, color:'#555', marginBottom:32, maxWidth:360, margin:'0 auto 32px', lineHeight:1.8 }}>
                Create a free account to unlock all {chapters.length} chapters of <em style={{ color:'#F0EDE6' }}>{book.title}</em> — plus 9 more books.
              </div>
              <button
                style={{ background:accent, color:'#080808', border:'none', borderRadius:12, padding:'14px 40px', fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:15, cursor:'pointer', marginBottom:14, display:'block', width:'100%', maxWidth:320, margin:'0 auto 14px' }}
                onClick={onSignUp}>
                Sign Up Free — It's Free →
              </button>
              <div style={{ fontSize:12, color:'#333' }}>No credit card required</div>
            </div>
          ) : (
            <div>
            <div style={{ marginBottom:36 }}>
            <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginBottom:12 }}>CHAPTER {chapter.number} OF {chapters.length}</div>
            <h2 style={{ ...serif, fontSize: isMobile?28:40, fontWeight:600, lineHeight:1.1 }}>{chapter.title}</h2>
          </div>
          <div style={{ color:'#777', fontSize: isMobile?15:16, lineHeight:2, marginBottom:36, letterSpacing:'0.01em' }}>{chapter.summary}</div>
          {chapter.quote && (
            <div style={{ borderLeft:`3px solid ${accent}`, paddingLeft:28, paddingTop:4, paddingBottom:4, margin:'36px 0', position:'relative' }}>
              <div style={{ position:'absolute', top:-16, left:14, ...serif, fontSize:80, color:accent, opacity:0.12, lineHeight:1 }}>"</div>
              <div style={{ ...serif, fontSize: isMobile?20:26, fontStyle:'italic', color:accent, lineHeight:1.55, marginBottom:12 }}>{chapter.quote}</div>
              {chapter.quote_author && <div style={{ fontSize:12, color:'#444', fontWeight:600, letterSpacing:1 }}>— {chapter.quote_author}</div>}
            </div>
          )}
          {chapterTakeaways.length > 0 && (
            <div style={{ background:'#0a0a0a', border:'1px solid #151515', borderRadius:16, padding: isMobile?'22px 20px':'28px 32px', margin:'36px 0' }}>
              <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:2, marginBottom:20 }}>KEY TAKEAWAYS</div>
              {chapterTakeaways.map((t, i) => (
                <div key={t.id} style={{ display:'flex', gap:14, marginBottom: i<chapterTakeaways.length-1?18:0 }}>
                  <div style={{ width:6, height:6, borderRadius:'50%', background:accent, flexShrink:0, marginTop:7 }} />
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, color:'#e0e0e0', marginBottom:4 }}>{t.title}</div>
                    <div style={{ color:'#555', fontSize:13, lineHeight:1.7 }}>{t.idea}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {chapter.insight && (
            <div style={{ background:`${accent}08`, border:`1px solid ${accent}22`, borderRadius:16, padding: isMobile?'22px 20px':'28px 32px', margin:'36px 0' }}>
              <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:2, marginBottom:12 }}>KEY INSIGHT</div>
              <div style={{ ...serif, fontSize: isMobile?18:22, fontStyle:'italic', color:accent, lineHeight:1.6 }}>{chapter.insight}</div>
            </div>
          )}
          {chapter.concepts && chapter.concepts.length > 0 && (
            <div style={{ margin:'36px 0' }}>
              <div style={{ fontSize:10, color:'#444', fontWeight:700, letterSpacing:2, marginBottom:16 }}>CORE CONCEPTS</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {chapter.concepts.map((c, i) => (
                  <div key={i} className="concept-card">
                    <div style={{ fontWeight:700, fontSize:14, color:'#e0e0e0', marginBottom:6 }}>{c.term}</div>
                    <div style={{ color:'#555', fontSize:13, lineHeight:1.65 }}>{c.definition}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {chapter.actionable_tip && (
            <div style={{ background:'#0f1a0f', border:'1px solid #27AE6033', borderRadius:16, padding: isMobile?'22px 20px':'28px 32px', margin:'36px 0' }}>
              <div style={{ fontSize:10, color:'#4ade80', fontWeight:700, letterSpacing:2, marginBottom:12 }}>THIS WEEK, TRY THIS</div>
              <div style={{ color:'#c8c8c8', fontSize: isMobile?14:15, lineHeight:1.75 }}>{chapter.actionable_tip}</div>
            </div>
          )}
          <div style={{ display:'flex', gap:10, marginTop:44 }}>
            {chapterIndex > 0 && (
              <button onClick={() => goTo(chapterIndex-1)}
                style={{ background:'transparent', border:'1px solid #252525', color:'#555', borderRadius:12, padding:'13px 22px', fontFamily:"'Outfit',sans-serif", fontWeight:500, fontSize:14, cursor:'pointer', flex:1 }}>
                ← Prev
              </button>
            )}
            <button onClick={markComplete}
              style={{ background: completedChapters.includes(chapter.id)?'#1a2a1a':accent, color: completedChapters.includes(chapter.id)?'#4ade80':'#080808', border: completedChapters.includes(chapter.id)?'1px solid #27AE6033':'none', borderRadius:12, padding:'13px 22px', fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:14, cursor:'pointer', flex:2 }}>
              {completedChapters.includes(chapter.id)
                ? (chapterIndex < chapters.length-1 ? '✓ Next Chapter →' : '✓ All Done!')
                : (chapterIndex < chapters.length-1 ? 'Mark Complete & Continue →' : 'Mark Complete ✓')}
            </button>
          </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [screen, setScreen]   = useState('landing');
  const [books, setBooks]     = useState([]);
  const [book, setBook]       = useState(null);
  const [mode, setMode]       = useState(null);
  const [search, setSearch]   = useState('');
  const [genre, setGenre]     = useState('All');
  const [sort, setSort]       = useState('popular');
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [user, setUser]                   = useState(null);
  const [authScreen, setAuthScreen]       = useState(null);
  const [authEmail, setAuthEmail]         = useState('');
  const [authPassword, setAuthPassword]   = useState('');
  const [authName, setAuthName]           = useState('');
  const [authError, setAuthError]         = useState('');
  const [authLoading, setAuthLoading]     = useState(false);
  const [authCustomMsg, setAuthCustomMsg] = useState('');
  const [forgotSent, setForgotSent]       = useState(false);
  const [isForgot, setIsForgot]           = useState(false);

  const [userProgress, setUserProgress]         = useState([]);
  const [userStreak, setUserStreak]             = useState(null);
  const [sessionLoggedToday, setSessionLoggedToday] = useState(false);

  const [chapters, setChapters]         = useState([]);
  const [takeaways, setTakeaways]       = useState([]);
  const [quizData, setQuizData]         = useState([]);
  const [flashData, setFlashData]       = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [quizLoading, setQuizLoading]   = useState(false);
  const [flashLoading, setFlashLoading] = useState(false);

  const [selectedChapter, setSelectedChapter] = useState('all');
  const [quizIndex, setQuizIndex] = useState(0);
  const [selected, setSelected]   = useState(null);
  const [showExp, setShowExp]     = useState(false);
  const [score, setScore]         = useState(0);
  const [quizDone, setQuizDone]   = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const [flashIndex, setFlashIndex]           = useState(0);
  const [flipped, setFlipped]                 = useState(false);
  const [flashMissed, setFlashMissed]         = useState([]);
  const [flashReviewMode, setFlashReviewMode] = useState(false);
  const [flashSessionDone, setFlashSessionDone] = useState(false);
  const [bookComplete, setBookComplete]       = useState(false);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // For guests on non-free books, limit to first FREE_CHAPTERS chapters
  const guestChapterIds = (!user && book && book.id !== FREE_BOOK)
    ? chapters.filter(c => c.number <= FREE_CHAPTERS).map(c => c.id)
    : null;

  const filteredQuiz  = selectedChapter === 'all'
    ? (guestChapterIds ? quizData.filter(q => guestChapterIds.includes(q.chapter_id)) : quizData)
    : quizData.filter(q => q.chapter_id === selectedChapter);

  const filteredFlash = selectedChapter === 'all'
    ? (guestChapterIds ? flashData.filter(f => guestChapterIds.includes(f.chapter_id)) : flashData)
    : flashData.filter(f => f.chapter_id === selectedChapter);

  const currentQ = filteredQuiz[quizIndex];

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (authScreen) return;
      if (screen === 'learn' && mode === 'flash' && !flashSessionDone) {
        if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          handleFlip();
        }
        if (e.key === 'ArrowRight' && flipped) handleFlashKnewIt();
        if (e.key === 'ArrowLeft' && flipped) handleFlashMissed();
      }
      if (screen === 'learn' && mode === 'quiz' && !quizDone && currentQ) {
        const n = parseInt(e.key);
        if (n >= 1 && n <= 4 && selected === null) handleAnswer(n - 1);
        if ((e.key === 'Enter' || e.key === 'ArrowRight') && selected !== null) nextQuestion();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, mode, authScreen, flipped, flashSessionDone, quizDone, currentQ, selected]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === 'SIGNED_IN') setScreen('dashboard');
      if (event === 'SIGNED_OUT') setScreen('landing');
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    supabase.from('books').select('*').order('title')
      .then(({ data }) => { setBooks(data || []); setLoading(false); });
  }, []);

  useEffect(() => {
    if (!user) { setUserProgress([]); setUserStreak(null); return; }
    supabase.from('user_progress').select('*').eq('user_id', user.id)
      .then(({ data }) => setUserProgress(data || []));
    supabase.from('user_streaks').select('*').eq('user_id', user.id).single()
      .then(({ data }) => {
        setUserStreak(data);
        if (data?.last_active === new Date().toISOString().split('T')[0]) setSessionLoggedToday(true);
      });
  }, [user]);

  const logSession = async () => {
    if (!user || sessionLoggedToday) return;
    const today = new Date().toISOString().split('T')[0];
    setSessionLoggedToday(true);
    if (userStreak) {
      const dayDiff = Math.floor((new Date(today) - new Date(userStreak.last_active)) / 86400000);
      if (dayDiff === 0) return;
      const newStreak = dayDiff === 1 ? userStreak.current_streak + 1 : 1;
      const longest = Math.max(newStreak, userStreak.longest_streak);
      const { data } = await supabase.from('user_streaks')
        .update({ current_streak: newStreak, longest_streak: longest, last_active: today, total_sessions: userStreak.total_sessions + 1 })
        .eq('user_id', user.id).select().single();
      if (data) setUserStreak(data);
    } else {
      const { data } = await supabase.from('user_streaks')
        .insert({ user_id: user.id, current_streak: 1, longest_streak: 1, last_active: today, total_sessions: 1 })
        .select().single();
      if (data) setUserStreak(data);
    }
  };

  const saveProgress = async (bookId, updates) => {
    if (!user) {
      if (bookId === FREE_BOOK) {
        const guestProg = JSON.parse(localStorage.getItem('guest_progress') || '{}');
        const merged = { ...guestProg, ...updates };
        localStorage.setItem('guest_progress', JSON.stringify(merged));
        if (merged.summary_viewed && merged.quiz_completed && merged.flashcards_started) {
          openAuth('signup', "🎉 You finished Extreme Ownership! Create a free account to unlock all 10 books.");
        }
      }
      return;
    }
    const existing = userProgress.find(p => p.book_id === bookId);
    let merged = existing ? { ...existing, ...updates } : updates;
    if (existing) {
      const { data } = await supabase.from('user_progress')
        .update({ ...updates, last_accessed: new Date().toISOString() })
        .eq('user_id', user.id).eq('book_id', bookId).select().single();
      if (data) { merged = data; setUserProgress(prev => prev.map(p => p.book_id === bookId ? data : p)); }
    } else {
      const { data } = await supabase.from('user_progress')
        .insert({ user_id: user.id, book_id: bookId, ...updates })
        .select().single();
      if (data) { merged = data; setUserProgress(prev => [...prev, data]); }
    }
    if (merged.summary_viewed && merged.quiz_completed && merged.flashcards_started) {
      const wasAlreadyComplete = existing?.summary_viewed && existing?.quiz_completed && existing?.flashcards_started;
      if (!wasAlreadyComplete) setBookComplete(true);
    }
  };

  useEffect(() => {
    if (!book) return;
    setContentLoading(true);
    Promise.all([
      supabase.from('chapters').select('*').eq('book_id', book.id).order('number'),
      supabase.from('takeaways').select('*').eq('book_id', book.id).order('sort_order'),
    ]).then(([chapRes, takeRes]) => {
      setChapters(chapRes.data || []);
      setTakeaways(takeRes.data || []);
      setContentLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book]);

  useEffect(() => {
    if (!book || mode !== 'quiz') return;
    setQuizLoading(true);
    setQuizData([]);
    supabase.from('quiz_questions')
      .select('*, chapters!inner(id, number, title, book_id)')
      .eq('chapters.book_id', book.id).order('sort_order')
      .then(({ data, error }) => {
        if (error) console.error('Quiz fetch error:', error);
        setQuizData(data || []); setQuizLoading(false);
      });
  }, [mode, book]);

  useEffect(() => {
    if (!book || mode !== 'flash') return;
    setFlashData([]); setFlashLoading(true);
    supabase.from('flashcards')
      .select('*, chapters!inner(id, number, title, book_id)')
      .eq('chapters.book_id', book.id).order('sort_order')
      .then(({ data, error }) => {
        if (error) console.error('Flash fetch error:', error);
        setFlashData(data || []); setFlashLoading(false);
      });
  }, [mode, book]);

  const openAuth = (type, customMsg='') => {
    setAuthScreen(type); setAuthError(''); setAuthEmail('');
    setAuthPassword(''); setAuthName(''); setAuthCustomMsg(customMsg);
    setIsForgot(false); setForgotSent(false);
  };
  const handleSignUp = async () => {
    setAuthLoading(true); setAuthError('');
    const { error } = await supabase.auth.signUp({ email: authEmail, password: authPassword, options: { data: { full_name: authName } } });
    if (error) { setAuthError(error.message); setAuthLoading(false); return; }
    setAuthScreen(null); setAuthLoading(false);
  };
  const handleLogin = async () => {
    setAuthLoading(true); setAuthError('');
    const { error } = await supabase.auth.signInWithPassword({ email: authEmail, password: authPassword });
    if (error) { setAuthError(error.message); setAuthLoading(false); return; }
    setAuthScreen(null); setAuthLoading(false); setIsForgot(false); setForgotSent(false);
  };
  const handleForgotPassword = async () => {
    setAuthLoading(true); setAuthError('');
    const { error } = await supabase.auth.resetPasswordForEmail(authEmail, { redirectTo: 'https://readifi.app' });
    if (error) { setAuthError(error.message); setAuthLoading(false); return; }
    setForgotSent(true); setAuthLoading(false);
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setBook(null); setMode(null); setSessionLoggedToday(false);
  };

  const openBook = b => {
    setBook(b); setScreen('book'); setMode(null);
    setChapters([]); setTakeaways([]);
  };
  const openMode = m => {
    setMode(m); setScreen('learn');
    setQuizIndex(0); setSelected(null); setShowExp(false);
    setScore(0); setQuizDone(false); setShowConfetti(false);
    setFlashIndex(0); setFlipped(false); setSelectedChapter('all');
    setFlashMissed([]); setFlashReviewMode(false); setFlashSessionDone(false);
    setBookComplete(false);
    if (m === 'summary' && book) saveProgress(book.id, { summary_viewed: true });
  };
  const back = () => {
    if (screen === 'learn')          { setScreen('book'); setMode(null); }
    else if (screen === 'book')      { setScreen('library'); setBook(null); }
    else if (screen === 'library')   { setScreen('landing'); }
    else if (screen === 'dashboard') { setScreen('landing'); }
  };
  const resetChapter = id => {
    setSelectedChapter(id);
    setQuizIndex(0); setSelected(null); setShowExp(false);
    setScore(0); setQuizDone(false); setShowConfetti(false);
    setFlashIndex(0); setFlipped(false);
  };

  const genres = ['All', ...new Set(books.map(b => b.genre))];
  const filtered = useMemo(() => {
    let list = books.filter(b =>
      (genre === 'All' || b.genre === genre) &&
      (b.title.toLowerCase().includes(search.toLowerCase()) ||
       b.author.toLowerCase().includes(search.toLowerCase()))
    );
    if (sort === 'alpha')   list = [...list].sort((a,b) => a.title.localeCompare(b.title));
    if (sort === 'newest')  list = [...list].sort((a,b) => b.year - a.year);
    if (sort === 'popular') list = [...list].sort((a,b) => POPULAR_ORDER.indexOf(a.id) - POPULAR_ORDER.indexOf(b.id));
    return list;
  }, [books, genre, search, sort]);

  const handleAnswer = idx => {
    if (selected !== null) return;
    setSelected(idx); setShowExp(true);
    if (idx === currentQ.correct_index) setScore(s => s + 1);
    const prog = userProgress.find(p => p.book_id === book.id);
    if (!prog?.quiz_started) saveProgress(book.id, { quiz_started: true });
    logSession();
  };

  const nextQuestion = () => {
    if (quizIndex + 1 >= filteredQuiz.length) {
      setScore(finalScore => {
        setQuizDone(true);
        if (finalScore / filteredQuiz.length >= 0.5) {
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 4500);
        }
        const existing = userProgress.find(p => p.book_id === book.id);
        const prevBest = existing?.quiz_score || 0;
        saveProgress(book.id, {
          quiz_started: true, quiz_completed: true, quiz_total: filteredQuiz.length,
          ...(finalScore > prevBest ? { quiz_score: finalScore } : {}),
        });
        return finalScore;
      });
      return;
    }
    setQuizIndex(i => i + 1); setSelected(null); setShowExp(false);
  };

  const handleFlip = () => {
    setFlipped(f => !f);
    const prog = userProgress.find(p => p.book_id === book?.id);
    if (!prog?.flashcards_started) saveProgress(book.id, { flashcards_started: true });
    logSession();
  };

  const handleFlashKnewIt = () => {
    const deck = flashReviewMode ? filteredFlash.filter((_,i) => flashMissed.includes(i)) : filteredFlash;
    const nextIndex = flashIndex + 1;
    if (nextIndex >= deck.length) { setFlashSessionDone(true); }
    else { setFlashIndex(nextIndex); setFlipped(false); }
  };

  const handleFlashMissed = () => {
    const deck = flashReviewMode ? filteredFlash.filter((_,i) => flashMissed.includes(i)) : filteredFlash;
    if (!flashReviewMode) setFlashMissed(prev => [...prev, flashIndex]);
    const nextIndex = flashIndex + 1;
    if (nextIndex >= deck.length) { setFlashSessionDone(true); }
    else { setFlashIndex(nextIndex); setFlipped(false); }
  };

  const startFlashReview = () => { setFlashReviewMode(true); setFlashIndex(0); setFlipped(false); setFlashSessionDone(false); setFlashMissed([]); };
  const resetFlashDeck   = () => { setFlashReviewMode(false); setFlashIndex(0); setFlipped(false); setFlashSessionDone(false); setFlashMissed([]); };

  const getProgressBadges = bookId => {
    const prog = userProgress.find(p => p.book_id === bookId);
    if (!prog) return [];
    const badges = [];
    if (prog.summary_viewed)     badges.push('Summary');
    if (prog.quiz_completed)     badges.push('Quiz');
    if (prog.flashcards_started) badges.push('Flashcards');
    return badges;
  };
  const isStarted = bookId => userProgress.find(p => p.book_id === bookId)?.quiz_started === true;

  const S = {
    page:  { minHeight:'100vh', background:'#080808', color:'#F0EDE6', fontFamily:"'Outfit', sans-serif" },
    serif: { fontFamily:"'Cormorant Garamond', serif" },
    btn:   (bg='#C8B96E', color='#080808') => ({ background:bg, color, border:'none', borderRadius:10, padding:'10px 22px', fontFamily:"'Outfit',sans-serif", fontWeight:600, fontSize:14, cursor:'pointer', transition:'all .2s' }),
    ghost: { background:'transparent', border:'1px solid #252525', color:'#777', borderRadius:10, padding:'10px 22px', fontFamily:"'Outfit',sans-serif", fontWeight:500, fontSize:14, cursor:'pointer', transition:'all .2s' },
    input: { background:'#0f0f0f', border:'1px solid #1e1e1e', color:'#F0EDE6', borderRadius:10, padding:'11px 16px', fontFamily:"'Outfit',sans-serif", fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' },
  };

  const accent = book?.palette?.[1] || '#C8B96E';
  const bookBg = book?.palette?.[0] || '#111';

  const authModal = authScreen ? (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.88)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20, backdropFilter:'blur(8px)', overflowY:'auto' }}
      onClick={e => { if (e.target === e.currentTarget) { setAuthScreen(null); setIsForgot(false); setForgotSent(false); } }}>
      <div className="pop-in" style={{ background:'#0f0f0f', border:'1px solid #1e1e1e', borderRadius:24, padding: isMobile?28:44, width:'100%', maxWidth:420, margin:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: authCustomMsg?16:36 }}>
          <span style={{ ...S.serif, fontSize:28, fontWeight:600, color:'#C8B96E' }}>
            {isForgot ? 'Reset Password' : authScreen==='signup' ? 'Create Account' : 'Welcome Back'}
          </span>
          <button onClick={() => { setAuthScreen(null); setIsForgot(false); setForgotSent(false); }} style={{ background:'none', border:'none', color:'#444', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>
        {authCustomMsg && !isForgot && (
          <div style={{ background:'#C8B96E0a', border:'1px solid #C8B96E18', borderRadius:10, padding:'10px 14px', marginBottom:24, fontSize:13, color:'#C8B96E' }}>{authCustomMsg}</div>
        )}
        {isForgot ? (
          forgotSent ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:48, marginBottom:16 }}>📬</div>
              <div style={{ fontSize:15, color:'#e0e0e0', marginBottom:8 }}>Check your inbox</div>
              <div style={{ fontSize:13, color:'#555', marginBottom:28 }}>We sent a reset link to <strong style={{ color:'#C8B96E' }}>{authEmail}</strong></div>
              <button style={{ ...S.ghost, width:'100%' }} onClick={() => { setIsForgot(false); setForgotSent(false); }}>Back to Sign In</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:20 }}>
                <label style={{ fontSize:11, color:'#444', letterSpacing:1.5, display:'block', marginBottom:8, fontWeight:600 }}>EMAIL</label>
                <input style={S.input} type="email" placeholder="you@email.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => e.key==='Enter' && handleForgotPassword()} />
              </div>
              {authError && <div style={{ background:'#C0392B12', border:'1px solid #C0392B33', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#e74c3c' }}>{authError}</div>}
              <button style={{ ...S.btn(), width:'100%', padding:15, fontSize:15, opacity:authLoading?0.6:1, marginBottom:14 }} onClick={handleForgotPassword} disabled={authLoading}>
                {authLoading ? 'Sending...' : 'Send Reset Link →'}
              </button>
              <button style={{ ...S.ghost, width:'100%' }} onClick={() => setIsForgot(false)}>← Back to Sign In</button>
            </>
          )
        ) : (
          <>
            {authScreen === 'signup' && (
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:'#444', letterSpacing:1.5, display:'block', marginBottom:8, fontWeight:600 }}>YOUR NAME</label>
                <input style={S.input} placeholder="Ben" value={authName} onChange={e => setAuthName(e.target.value)} />
              </div>
            )}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11, color:'#444', letterSpacing:1.5, display:'block', marginBottom:8, fontWeight:600 }}>EMAIL</label>
              <input style={S.input} type="email" placeholder="you@email.com" value={authEmail} onChange={e => setAuthEmail(e.target.value)} onKeyDown={e => e.key==='Enter'&&(authScreen==='login'?handleLogin():handleSignUp())} />
            </div>
            <div style={{ marginBottom: authScreen==='login'?12:28 }}>
              <label style={{ fontSize:11, color:'#444', letterSpacing:1.5, display:'block', marginBottom:8, fontWeight:600 }}>PASSWORD</label>
              <input style={S.input} type="password" placeholder="••••••••" value={authPassword} onChange={e => setAuthPassword(e.target.value)} onKeyDown={e => e.key==='Enter'&&(authScreen==='login'?handleLogin():handleSignUp())} />
            </div>
            {authScreen === 'login' && (
              <div style={{ textAlign:'right', marginBottom:20 }}>
                <span style={{ fontSize:12, color:'#555', cursor:'pointer' }} onClick={() => { setIsForgot(true); setAuthError(''); }}>Forgot password?</span>
              </div>
            )}
            {authError && <div style={{ background:'#C0392B12', border:'1px solid #C0392B33', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#e74c3c' }}>{authError}</div>}
            <button style={{ ...S.btn(), width:'100%', padding:15, fontSize:15, opacity:authLoading?0.6:1 }} onClick={authScreen==='login'?handleLogin:handleSignUp} disabled={authLoading}>
              {authLoading?'Please wait...':authScreen==='signup'?'Create Account →':'Sign In →'}
            </button>
            <div style={{ textAlign:'center', marginTop:24, fontSize:13, color:'#444' }}>
              {authScreen==='signup'
                ? <>Already have an account?{' '}<span style={{ color:'#C8B96E', cursor:'pointer' }} onClick={() => {setAuthScreen('login');setAuthError('');setAuthCustomMsg('');}}>Sign in</span></>
                : <>Don't have an account?{' '}<span style={{ color:'#C8B96E', cursor:'pointer' }} onClick={() => {setAuthScreen('signup');setAuthError('');}}>Sign up free</span></>}
            </div>
            {authScreen==='signup' && <div style={{ marginTop:20, padding:'12px 16px', background:'#C8B96E08', border:'1px solid #C8B96E18', borderRadius:10, fontSize:12, color:'#444', textAlign:'center' }}>🔒 Free account — no credit card required</div>}
          </>
        )}
      </div>
    </div>
  ) : null;

  const Nav = ({ backLabel, backFn, accentColor='#C8B96E' }) => (
    <nav style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding: isMobile?'16px 20px':'18px 40px', borderBottom:'1px solid #111', position:'sticky', top:0, background:'rgba(8,8,8,0.94)', backdropFilter:'blur(16px)', zIndex:99 }}>
      {backFn ? <button style={{ ...S.ghost, fontSize:13, padding:'7px 16px' }} onClick={backFn}>{backLabel}</button> : <div />}
      <span style={{ ...S.serif, fontSize:22, color:accentColor, letterSpacing:3, cursor:'pointer', fontWeight:600 }} onClick={() => setScreen(user?'dashboard':'landing')}>READIFI</span>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        {user ? (
          <>
            {!isMobile && <button style={{ ...S.ghost, fontSize:13, padding:'7px 14px' }} onClick={() => setScreen('dashboard')}>Dashboard</button>}
            <button style={{ ...S.ghost, fontSize:13, padding:'7px 14px', color:'#444' }} onClick={handleLogout}>Sign Out</button>
          </>
        ) : (
          <>
            <button style={{ ...S.ghost, fontSize:13, padding:'7px 14px' }} onClick={() => openAuth('login')}>Sign In</button>
            <button style={{ ...S.btn(), fontSize:13, padding:'8px 18px' }} onClick={() => openAuth('signup')}>{isMobile?'Sign Up':'Sign Up Free'}</button>
          </>
        )}
      </div>
    </nav>
  );

  if (screen === 'dashboard' && user) {
    const inProgress = books.filter(b => isStarted(b.id));
    const notStarted = books.filter(b => !isStarted(b.id));
    const completed = books.filter(b => {
      const p = userProgress.find(x => x.book_id === b.id);
      return p?.summary_viewed && p?.quiz_completed && p?.flashcards_started;
    });
    const streak=userStreak?.current_streak||0, longest=userStreak?.longest_streak||0, sessions=userStreak?.total_sessions||0;
    const userName = user.user_metadata?.full_name?.split(' ')[0] || 'there';
    const isFirstTime = inProgress.length === 0 && sessions === 0;
    const streakSub = streak >= 7 ? "you're on fire 🔥" : streak >= 3 ? "don't break it today" : streak === 1 ? "great start!" : "days in a row";
    return (
      <div style={S.page} className="screen-enter">
        <style>{FONTS}</style>
        {authModal}
        <Nav backLabel="← Home" backFn={() => setScreen('landing')} />
        <div style={{ maxWidth:1100, margin:'0 auto', padding: isMobile?'32px 16px':'52px 40px' }}>
          <div style={{ marginBottom:52 }} className="fade-up">
            <div style={{ fontSize:11, color:'#444', letterSpacing:3, marginBottom:10, fontWeight:600 }}>{isFirstTime ? 'WELCOME' : 'WELCOME BACK'}</div>
            <h1 style={{ ...S.serif, fontSize: isMobile?40:56, fontWeight:300, lineHeight:1 }}>Hey, <em style={{ color:'#C8B96E' }}>{userName}.</em></h1>
            <p style={{ color:'#555', marginTop:10, fontSize:15 }}>
              {isFirstTime ? 'Your reading retention journey starts now.' : streak > 0 ? `${streak}-day streak — keep it going.` : 'Ready to learn something today?'}
            </p>
          </div>
          {isFirstTime && (
            <div className="fade-up" style={{ background:'linear-gradient(135deg,#0f0f0f,#1a140a)', border:'1px solid #C8B96E22', borderRadius:20, padding: isMobile?'28px 24px':'36px 40px', marginBottom:40, display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ fontSize:48 }}>📖</div>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ fontWeight:700, fontSize:18, marginBottom:6, color:'#F0EDE6' }}>Start with Atomic Habits</div>
                <div style={{ fontSize:14, color:'#555', marginBottom:18 }}>It's free — no payment needed. Takes about 20 minutes to complete all three modes.</div>
                <button style={{ ...S.btn(), fontSize:14 }} onClick={() => openBook(books.find(b => b.id === 'atomic-habits') || books[0])}>Begin Learning →</button>
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr 1fr':'repeat(4,1fr)', gap:10, marginBottom:52 }}>
            {[
              {label:'Streak', value:`${streak} 🔥`, sub: streakSub},
              {label:'Best Streak', value:`${longest} 🏆`, sub:'personal best'},
              {label:'Books Completed', value:completed.length, sub:`of ${books.length} total`},
              {label:'Sessions', value:sessions, sub:'learning days'},
            ].map(({label,value,sub},i) => (
              <div key={label} className="fade-up" style={{ animationDelay:`${i*.08}s`, background:'#0a0a0a', border:'1px solid #111', borderRadius:16, padding: isMobile?'16px 14px':'22px 24px' }}>
                <div style={{ fontSize:10, color:'#555', letterSpacing:2, marginBottom:10, fontWeight:600 }}>{label.toUpperCase()}</div>
                <div style={{ ...S.serif, fontSize: isMobile?30:38, color:'#C8B96E', fontWeight:600, lineHeight:1 }}>{value}</div>
                <div style={{ fontSize:11, color:'#555', marginTop:6 }}>{sub}</div>
              </div>
            ))}
          </div>
          {inProgress.length > 0 && (
            <div style={{ marginBottom:52 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
                <h2 style={{ ...S.serif, fontSize:30, fontWeight:400 }}>Continue Reading</h2>
                <button style={{ ...S.ghost, fontSize:13, padding:'7px 14px' }} onClick={() => setScreen('library')}>All books →</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'repeat(auto-fill,minmax(320px,1fr))', gap:14 }}>
                {inProgress.map(b => {
                  const badges=getProgressBadges(b.id), prog=userProgress.find(p=>p.book_id===b.id);
                  return (
                    <div key={b.id} onClick={() => openBook(b)} className="mode-card" style={{ background:`linear-gradient(135deg,#0f0f0f,${b.palette[0]}44)`, border:`1px solid ${b.palette[1]}18`, display:'flex', gap:18, alignItems:'flex-start' }}>
                      <div style={{ fontSize:40, flexShrink:0 }}>{b.emoji}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ ...S.serif, fontSize:19, fontWeight:600, marginBottom:3, lineHeight:1.2 }}>{b.title}</div>
                        <div style={{ fontSize:12, color:'#555', marginBottom:14 }}>{b.author}</div>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
                          {['Summary','Quiz','Flashcards'].map(m => (<span key={m} style={{ fontSize:10, borderRadius:100, padding:'3px 10px', fontWeight:600, background:badges.includes(m)?'#27AE6018':'#ffffff06', color:badges.includes(m)?'#4ade80':'#444', border:`1px solid ${badges.includes(m)?'#27AE6033':'#2a2a2a'}` }}>{badges.includes(m)?`✓ ${m}`:m}</span>))}
                        </div>
                        {prog?.quiz_completed && <div style={{ fontSize:11, color:'#4ade80' }}>Quiz: {prog.quiz_score}/{prog.quiz_total} correct</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
              <h2 style={{ ...S.serif, fontSize:30, fontWeight:400 }}>{inProgress.length>0?'Explore More':'Start Learning'}</h2>
              <button style={{ ...S.btn(), fontSize:13, padding:'8px 18px' }} onClick={() => setScreen('library')}>Browse Library →</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns: isMobile?'repeat(2,1fr)':'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
              {notStarted.slice(0,isMobile?4:6).map(b => (
                <div key={b.id} onClick={() => openBook(b)} className="mode-card" style={{ padding:'22px 20px' }}>
                  <div style={{ fontSize:30, marginBottom:12 }}>{b.emoji}</div>
                  <div style={{ fontSize:13, fontWeight:600, lineHeight:1.3, marginBottom:4 }}>{b.title}</div>
                  <div style={{ fontSize:11, color:'#555' }}>{b.author}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'landing') return (
    <div style={S.page} className="screen-enter">
      <style>{FONTS}</style>
      {authModal}
      <nav style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding: isMobile?'16px 20px':'20px 48px', borderBottom:'1px solid #111', position:'sticky', top:0, background:'rgba(8,8,8,0.94)', backdropFilter:'blur(16px)', zIndex:100 }}>
        <span style={{ ...S.serif, fontSize:24, fontWeight:600, color:'#C8B96E', letterSpacing:3 }}>READIFI</span>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          {!isMobile && <span style={{ color:'#555', fontSize:14, cursor:'pointer' }} onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})}>How It Works</span>}
          {user
            ? <button style={{ ...S.btn(), padding:'9px 20px' }} onClick={() => setScreen('dashboard')}>Dashboard →</button>
            : <>
                <button style={{ ...S.ghost, padding:'8px 16px', fontSize:13 }} onClick={() => openAuth('login')}>Sign In</button>
                <button style={{ ...S.btn(), padding:'9px 20px', fontSize:13 }} onClick={() => setScreen('library')}>{isMobile?'Library →':'Open the Library →'}</button>
              </>
          }
        </div>
      </nav>
      <div style={{ textAlign:'center', padding: isMobile?'64px 20px 52px':'120px 24px 90px', maxWidth:960, margin:'0 auto', position:'relative' }}>
        <div style={{ position:'absolute', top:80, left:'5%', width:500, height:500, background:'#C8B96E05', borderRadius:'50%', filter:'blur(100px)', pointerEvents:'none' }} />
        <div className="fade-up" style={{ display:'inline-block', background:'#C8B96E10', border:'1px solid #C8B96E22', borderRadius:100, padding:'6px 22px', fontSize:11, color:'#C8B96E', marginBottom:40, fontWeight:700, letterSpacing:3 }}>READ · RETAIN · RECALL</div>
        <h1 className="fade-up" style={{ ...S.serif, fontSize:'clamp(52px,8vw,104px)', fontWeight:300, lineHeight:0.95, margin:'0 0 36px', animationDelay:'.1s' }}>Books that actually<br /><em style={{ color:'#C8B96E' }}>stick with you.</em></h1>
        <p className="fade-up" style={{ fontSize:18, color:'#555', maxWidth:520, margin:'0 auto 56px', lineHeight:1.85, animationDelay:'.2s' }}>You forget 70% of what you read within 24 hours. Readifi fixes that — turning the books you read into knowledge you actually keep.</p>
        <div className="fade-up" style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap', marginBottom:90, animationDelay:'.3s' }}>
          <button style={{ ...S.btn(), padding:'15px 44px', fontSize:16 }} onClick={() => openAuth('signup')}>Start Learning Free →</button>
          <button style={{ ...S.ghost, padding:'15px 44px', fontSize:16, color:'#555' }} onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})}>See How It Works ↓</button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr 1fr':'repeat(4,1fr)', borderTop:'1px solid #111', borderBottom:'1px solid #111', padding: isMobile?'24px 0':'36px 0' }}>
          {[['10','Books'],['200+','Quiz Questions'],['100+','Flashcards'],['3','Learning Modes']].map(([n,l],i) => (
            <div key={l} style={{ textAlign:'center', padding: isMobile?'16px 8px':'0 40px', borderRight: isMobile?(i%2===0?'1px solid #111':'none'):(i<3?'1px solid #111':'none'), borderBottom: isMobile&&i<2?'1px solid #111':'none' }}>
              <div style={{ ...S.serif, fontSize: isMobile?34:48, color:'#C8B96E', fontWeight:600, lineHeight:1 }}>{n}</div>
              <div style={{ fontSize:11, color:'#666', marginTop:10, letterSpacing:2, fontWeight:600 }}>{l.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background:'#050505', borderTop:'1px solid #111', borderBottom:'1px solid #111', padding: isMobile?'64px 20px':'110px 24px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap: isMobile?48:90, alignItems:'center' }}>
            <div>
              <div style={{ fontSize:10, color:'#C0392B', fontWeight:700, letterSpacing:3, marginBottom:24 }}>THE SCIENCE</div>
              <h2 style={{ ...S.serif, fontSize:'clamp(36px,5vw,54px)', fontWeight:300, lineHeight:1.1, marginBottom:28 }}>Your brain is<br /><em style={{ color:'#C0392B' }}>designed to forget.</em></h2>
              <p style={{ color:'#555', fontSize:15, lineHeight:1.9, marginBottom:28 }}>Hermann Ebbinghaus discovered the <strong style={{ color:'#777' }}>Forgetting Curve</strong> — memory decays exponentially without reinforcement.</p>
              <p style={{ color:'#555', fontSize:15, lineHeight:1.9, marginBottom:36 }}>The fix is <strong style={{ color:'#777' }}>active recall</strong> — retrieving information from memory forces your brain to strengthen that pathway every time.</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {[['Passive reading','5–10% after 1 week','#C0392B'],['Re-reading','20–30% after 1 week','#E67E22'],['Active recall + spaced repetition','80–90% after 1 week','#27AE60']].map(([m,r,c]) => (
                  <div key={m} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#0a0a0a', border:'1px solid #111', borderRadius:10, padding:'12px 16px', gap:16 }}>
                    <span style={{ fontSize:13, color:'#555' }}>{m}</span>
                    <span style={{ fontSize:11, color:c, fontWeight:700, whiteSpace:'nowrap' }}>{r}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background:'#0a0a0a', border:'1px solid #111', borderRadius:20, padding:36 }}>
              <div style={{ fontSize:10, color:'#444', letterSpacing:2, marginBottom:24, fontWeight:600 }}>EBBINGHAUS FORGETTING CURVE</div>
              <div style={{ position:'relative', height:200 }}>
                <div style={{ position:'absolute', left:0, top:0, bottom:0, display:'flex', flexDirection:'column', justifyContent:'space-between', paddingRight:12 }}>
                  {['100%','75%','50%','25%','0%'].map(v => <span key={v} style={{ fontSize:10, color:'#333' }}>{v}</span>)}
                </div>
                <div style={{ marginLeft:36, height:'100%' }}>
                  <svg width="100%" height="100%" viewBox="0 0 300 180" preserveAspectRatio="none">
                    {[0,45,90,135,180].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#151515" strokeWidth="1" />)}
                    <path d="M0,0 C30,40 60,100 100,140 C140,165 200,172 300,175" fill="none" stroke="#C0392B" strokeWidth="2" strokeDasharray="6,3" />
                    <path d="M0,0 C20,15 50,30 100,50 C160,72 220,85 300,90" fill="none" stroke="#27AE60" strokeWidth="2.5" />
                    <path d="M0,0 C20,15 50,30 100,50 C160,72 220,85 300,90 L300,180 L0,180 Z" fill="#27AE600a" />
                  </svg>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginLeft:36, marginTop:8 }}>
                {['Now','1hr','1day','1wk','1mo'].map(t => <span key={t} style={{ fontSize:10, color:'#333' }}>{t}</span>)}
              </div>
              <div style={{ display:'flex', gap:20, marginTop:20, justifyContent:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}><div style={{ width:20, height:2, borderTop:'2px dashed #C0392B' }} /><span style={{ fontSize:11, color:'#444' }}>Without recall</span></div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}><div style={{ width:20, height:2, background:'#27AE60' }} /><span style={{ fontSize:11, color:'#444' }}>With Readifi</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="how-it-works" style={{ padding: isMobile?'64px 20px':'110px 24px', maxWidth:1000, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:72 }}>
          <div style={{ fontSize:10, color:'#C8B96E', fontWeight:700, letterSpacing:3, marginBottom:20 }}>THE METHOD</div>
          <h2 style={{ ...S.serif, fontSize:'clamp(36px,5vw,54px)', fontWeight:300, marginBottom:20 }}>Three modes. One system.</h2>
          <p style={{ color:'#444', fontSize:16, maxWidth:480, margin:'0 auto' }}>Each mode targets a different stage of the learning and retention cycle.</p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:2, background:'#111', borderRadius:24, overflow:'hidden' }}>
          {[{n:'01',icon:'📖',title:'Deep Summary',desc:'Book overview, chapter summaries, key quotes, concepts, and an actionable tip — all in one beautiful reader.',badge:'Foundation'},{n:'02',icon:'🃏',title:'Flashcards',desc:'Key terms and frameworks drilled with a smooth 3D flip. Moves knowledge into long-term memory.',badge:'Long-Term Memory'},{n:'03',icon:'✅',title:'Quiz Mode',desc:'Chapter-by-chapter questions that force active recall — the most effective retention technique known to science.',badge:'Active Recall'}].map(({n,icon,title,desc,badge}) => (
            <div key={n} style={{ background:'#0a0a0a', padding: isMobile?'36px 28px':'44px 36px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28 }}>
                <span style={{ fontSize:36 }}>{icon}</span>
                <span style={{ fontSize:9, background:'#C8B96E10', color:'#C8B96E', border:'1px solid #C8B96E18', borderRadius:100, padding:'4px 10px', fontWeight:700, letterSpacing:1.5 }}>{badge.toUpperCase()}</span>
              </div>
              <div style={{ fontSize:10, color:'#444', fontWeight:700, letterSpacing:2, marginBottom:12 }}>{n}</div>
              <div style={{ fontSize:18, fontWeight:600, marginBottom:14 }}>{title}</div>
              <div style={{ color:'#444', fontSize:13, lineHeight:1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ textAlign:'center', padding: isMobile?'64px 20px':'110px 24px' }}>
        <h2 style={{ ...S.serif, fontSize:'clamp(40px,6vw,68px)', fontWeight:300, marginBottom:28, lineHeight:1.05 }}>Ready to actually remember<br /><em style={{ color:'#C8B96E' }}>what you read?</em></h2>
        <p style={{ color:'#444', fontSize:16, maxWidth:420, margin:'0 auto 44px' }}>Ten of the most important books ever written. Summarized, quizzed, and drilled. Free to start.</p>
        <button style={{ ...S.btn(), padding:'16px 56px', fontSize:17 }} onClick={() => openAuth('signup')}>Create Free Account →</button>
        <div style={{ marginTop:18 }}><span style={{ color:'#555', fontSize:13, cursor:'pointer' }} onClick={() => setScreen('library')}>Or browse the library first →</span></div>
      </div>
      <footer style={{ borderTop:'1px solid #111', padding: isMobile?'24px 20px':'32px 48px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:16 }}>
        <span style={{ ...S.serif, fontSize:20, color:'#C8B96E', letterSpacing:3 }}>READIFI</span>
        <span style={{ color:'#444', fontSize:13 }}>Read. Retain. Recall.</span>
      </footer>
    </div>
  );

  if (screen === 'library') return (
    <div style={S.page} className="screen-enter">
      <style>{FONTS}</style>
      {authModal}
      <Nav backLabel="← Home" backFn={back} />
      <div style={{ maxWidth:1100, margin:'0 auto', padding: isMobile?'32px 16px':'52px 32px' }}>
        <h1 style={{ ...S.serif, fontSize: isMobile?40:52, fontWeight:300, marginBottom:8 }}>The Library</h1>
        <p style={{ color:'#555', marginBottom:44 }}>Ten books. Every key idea. Fully retained.</p>
        {!user && (
          <div style={{ background:'#C8B96E08', border:'1px solid #C8B96E18', borderRadius:14, padding:'16px 22px', marginBottom:32, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <div>
              <span style={{ fontSize:13, color:'#C8B96E', fontWeight:700 }}>🔓 Atomic Habits is free — no account needed.</span>
              <span style={{ fontSize:13, color:'#555', marginLeft: isMobile?0:10, display: isMobile?'block':'inline', marginTop: isMobile?4:0 }}>Sign up free to unlock all 10 books.</span>
            </div>
            <button style={{ ...S.btn(), fontSize:13, padding:'8px 18px' }} onClick={() => openAuth('signup')}>Sign Up Free →</button>
          </div>
        )}
        <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:44 }}>
          <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
            <input style={{ ...S.input, maxWidth:300 }} placeholder="Search books or authors..." value={search} onChange={e => setSearch(e.target.value)} />
            <div style={{ display:'flex', gap:2, background:'#0a0a0a', border:'1px solid #181818', borderRadius:10, padding:3 }}>
              {[{id:'popular',label:'🔥 Popular'},{id:'newest',label:'🆕 Newest'},{id:'alpha',label:'🔤 A → Z'}].map(s => (
                <button key={s.id} onClick={() => setSort(s.id)} style={{ background:sort===s.id?'#C8B96E':'transparent', color:sort===s.id?'#080808':'#555', border:'none', borderRadius:8, padding:'7px 14px', fontSize:12, fontWeight:sort===s.id?700:400, cursor:'pointer', fontFamily:"'Outfit',sans-serif", transition:'all .15s' }}>{s.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:11, color:'#555', letterSpacing:1.5, fontWeight:600 }}>GENRE:</span>
            {genres.map(g => (<button key={g} onClick={() => setGenre(g)} style={{ background:genre===g?'#C8B96E':'transparent', color:genre===g?'#080808':'#555', border:`1px solid ${genre===g?'#C8B96E':'#2a2a2a'}`, borderRadius:100, padding:'5px 16px', fontSize:12, fontWeight:genre===g?700:400, cursor:'pointer', fontFamily:"'Outfit',sans-serif", transition:'all .15s' }}>{g}</button>))}
          </div>
          <div style={{ fontSize:12, color:'#555' }}>{filtered.length} {filtered.length===1?'book':'books'}{genre!=='All'?` in ${genre}`:''}{search?` matching "${search}"`:''}</div>
        </div>
        {loading ? <div style={{ color:'#555', padding:48, textAlign:'center' }}>Loading library...</div>
        : filtered.length===0 ? <div style={{ color:'#555', padding:48, textAlign:'center' }}>No books found for "{search}"</div>
        : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:16 }}>
            {filtered.map(b => {
              const isFreeBook = b.id === FREE_BOOK;
              const isGuestLimited = !user && !isFreeBook;
              const badges = getProgressBadges(b.id);
              const prog = userProgress.find(p => p.book_id === b.id);
              const chapDone = prog?.completed_chapters?.length || 0;
              const SEEDED_BOOKS = ['atomic-habits','deep-work','extreme-ownership','48-laws-power','iwt-rich'];
              const isComingSoon = !SEEDED_BOOKS.includes(b.id);
              return (
                <div key={b.id} onClick={() => openBook(b)} className="mode-card" style={{ background:`linear-gradient(140deg,#0f0f0f,${b.palette[0]}44)`, border:`1px solid ${b.palette[1]+'18'}`, position:'relative' }}>
                  {isComingSoon && <div style={{ position:'absolute', top:16, right:16, fontSize:9, background:'#C8B96E15', border:'1px solid #C8B96E30', color:'#C8B96E', borderRadius:100, padding:'3px 8px', fontWeight:700, letterSpacing:1 }}>SOON</div>}
                  {isGuestLimited && !isComingSoon && <div style={{ position:'absolute', top:16, right:16, fontSize:9, background:'#ffffff08', border:'1px solid #2a2a2a', color:'#555', borderRadius:100, padding:'3px 8px', fontWeight:700, letterSpacing:1 }}>2 FREE</div>}
                  <div style={{ fontSize:36, marginBottom:16 }}>{b.emoji}</div>
                  <div style={{ fontSize:10, color:b.palette[1], fontWeight:700, letterSpacing:2, marginBottom:10 }}>{b.genre.toUpperCase()}</div>
                  <div style={{ ...S.serif, fontSize:22, fontWeight:600, lineHeight:1.2, marginBottom:6 }}>{b.title}</div>
                  <div style={{ fontSize:13, color:'#555', marginBottom:16 }}>{b.author} · {b.year}</div>
                  <div style={{ fontSize:13, color:'#555', lineHeight:1.65, marginBottom:18 }}>{b.description}</div>
                  {isComingSoon ? (
                    <span style={{ fontSize:12, color:'#555' }}>Content arriving soon — check back!</span>
                  ) : isGuestLimited ? (
                    <span style={{ fontSize:12, color:'#555' }}>First 2 chapters free · <span style={{ color:'#C8B96E' }}>Sign up for full access →</span></span>
                  ) : badges.length > 0 ? (
                    <div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom: chapDone>0?8:0 }}>
                        {['Summary','Quiz','Flashcards'].map(m => (<span key={m} style={{ fontSize:10, borderRadius:100, padding:'3px 10px', fontWeight:600, background:badges.includes(m)?'#27AE6018':'#ffffff06', color:badges.includes(m)?'#4ade80':'#444', border:`1px solid ${badges.includes(m)?'#27AE6033':'#2a2a2a'}` }}>{badges.includes(m)?`✓ ${m}`:m}</span>))}
                      </div>
                      {chapDone > 0 && <div style={{ fontSize:11, color:'#555', marginTop:6 }}>📖 {chapDone} chapter{chapDone!==1?'s':''} read</div>}
                    </div>
                  ) : (
                    <div style={{ display:'flex', gap:6 }}>
                      {['Summary','Quiz','Flashcards'].map(m => (<span key={m} style={{ fontSize:10, background:'#ffffff06', borderRadius:100, padding:'3px 10px', color:'#444', border:'1px solid #2a2a2a' }}>{m}</span>))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (screen === 'book' && book) {
    const prog = userProgress.find(p => p.book_id === book.id);
    const chapDone = prog?.completed_chapters?.length || 0;
    const chapTotal = chapters.length;

    // Summary progress label
    const summaryPct = chapTotal > 0 ? Math.round((chapDone / chapTotal) * 100) : 0;
    const summaryDone = summaryPct === 100;
    const summarySub = chapTotal > 0
      ? (summaryDone ? 'All chapters complete' : chapDone > 0 ? `${chapDone}/${chapTotal} chapters · ${summaryPct}%` : 'Overview · Chapters · Quotes · Concepts · Tips')
      : 'Overview · Chapters · Quotes · Concepts · Tips';

    // Quiz progress label
    const quizBestLabel = prog?.quiz_completed && prog?.quiz_total
      ? `Best: ${prog.quiz_score}/${prog.quiz_total} · ${Math.round((prog.quiz_score/prog.quiz_total)*100)}%`
      : null;

    // Flashcard progress label — count how many chapter decks touched vs total
    const flashSub = prog?.flashcards_started ? 'Deck completed — review anytime' : 'Drill key terms with 3D flip cards';

    const modes = [
      { id:'summary', icon:'📖', label:'Deep Summary',  sub: summarySub, done: summaryDone, started: chapDone > 0, pct: summaryPct, time:'~15 min' },
      { id:'flash',   icon:'🃏', label:'Flashcards',    sub: flashSub,   done: prog?.flashcards_started, started: prog?.flashcards_started, pct: null, time:'~5 min'  },
      { id:'quiz',    icon:'✅', label:'Quiz Mode',     sub: quizBestLabel || 'Test your recall — conceptual questions', done: prog?.quiz_completed, started: prog?.quiz_started, pct: null, time:'~8 min' },
    ];
    return (
      <div style={S.page} className="screen-enter">
        <style>{FONTS}</style>
        {authModal}
        <Nav backLabel="← Library" backFn={back} accentColor={accent} />
        <div style={{ background:`linear-gradient(180deg,${bookBg}33 0%,#080808 100%)`, borderBottom:'1px solid #111', padding: isMobile?'40px 20px 48px':'64px 40px 72px' }}>
          <div style={{ maxWidth:780, margin:'0 auto' }}>
            <div className="fade-up" style={{ display:'flex', gap: isMobile?20:32, alignItems:'flex-start', flexWrap: isMobile?'wrap':'nowrap' }}>
              <div style={{ fontSize: isMobile?64:88, flexShrink:0, filter:'drop-shadow(0 8px 24px rgba(0,0,0,0.5))' }}>{book.emoji}</div>
              <div>
                <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginBottom:12 }}>{book.genre.toUpperCase()}</div>
                <h1 style={{ ...S.serif, fontSize: isMobile?32:48, fontWeight:600, lineHeight:1.05, margin:'0 0 10px' }}>{book.title}</h1>
                <div style={{ color:'#555', marginBottom:18, fontSize:15 }}>{book.author} · {book.year}</div>
                <p style={{ color:'#444', lineHeight:1.75, fontSize:15, maxWidth:520 }}>{book.description}</p>
              </div>
            </div>
          </div>
        </div>
        <div style={{ maxWidth:780, margin:'0 auto', padding: isMobile?'32px 16px':'52px 40px' }}>
          <div style={{ fontSize:11, color:'#555', letterSpacing:2, marginBottom:28, fontWeight:600 }}>CHOOSE YOUR MODE</div>
          {contentLoading ? (
            <div style={{ color:'#555', padding:64, textAlign:'center', fontSize:14 }}>
              <div style={{ fontSize:32, marginBottom:16 }}>📖</div>
              Loading content...
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {modes.map((m, i) => (
                <div key={m.id} onClick={() => openMode(m.id)} className="mode-card fade-up" style={{ animationDelay:`${i*.1}s`, border:`1px solid ${m.done?accent+'33':m.started?accent+'18':'#1a1a1a'}`, display:'flex', alignItems:'center', gap:20, padding: isMobile?'20px 22px':'24px 32px' }}>
                  <div style={{ fontSize: isMobile?32:40, flexShrink:0 }}>{m.icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:700, fontSize: isMobile?16:18 }}>{m.label}</span>
                      {m.done
                        ? <span style={{ fontSize:10, background:'#27AE6018', color:'#4ade80', border:'1px solid #27AE6033', borderRadius:100, padding:'2px 10px', fontWeight:700 }}>✓ Complete</span>
                        : m.started && m.pct != null && m.pct > 0
                          ? <span style={{ fontSize:10, background:`${accent}12`, color:accent, border:`1px solid ${accent}30`, borderRadius:100, padding:'2px 10px', fontWeight:700 }}>{m.pct}%</span>
                          : m.started
                            ? <span style={{ fontSize:10, background:`${accent}12`, color:accent, border:`1px solid ${accent}30`, borderRadius:100, padding:'2px 10px', fontWeight:700 }}>In Progress</span>
                            : null}
                    </div>
                    <div style={{ fontSize:13, color:'#555' }}>{m.sub}</div>
                    {/* Summary progress bar */}
                    {m.id === 'summary' && m.pct != null && m.pct > 0 && !m.done && (
                      <div style={{ marginTop:10 }}>
                        <div className="progress-track" style={{ height:2 }}>
                          <div className="progress-fill" style={{ width:`${m.pct}%`, background:accent }} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:11, color:'#555', marginBottom:6 }}>{m.time}</div>
                    <div style={{ fontSize:13, color:m.done?'#4ade80':accent, fontWeight:600 }}>{m.done?'Review →':m.started?'Continue →':'Start →'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (screen === 'learn' && book && mode) {
    const ChapterFilter = () => {
      const isGuestLimited = !user && book.id !== FREE_BOOK;
      const visibleChapters = isGuestLimited ? chapters.filter(c => c.number <= FREE_CHAPTERS) : chapters;
      return (
        <div style={{ marginBottom:36 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:10, color:'#555', letterSpacing:2, fontWeight:600 }}>FILTER BY CHAPTER</div>
            {isGuestLimited && <div style={{ fontSize:11, color:'#C8B96E', cursor:'pointer' }} onClick={() => openAuth('signup', `Sign up free to unlock all ${chapters.length} chapters.`)}>Unlock all {chapters.length} chapters →</div>}
          </div>
          <div className="pill-scroll">
            <button onClick={() => resetChapter('all')} className={`chapter-pill${selectedChapter==='all'?' active':''}`} style={{ borderColor:selectedChapter==='all'?accent:undefined, color:selectedChapter==='all'?accent:undefined, flexShrink:0 }}>All Chapters</button>
            {visibleChapters.map(c => (<button key={c.id} onClick={() => resetChapter(c.id)} className={`chapter-pill${selectedChapter===c.id?' active':''}`} style={{ borderColor:selectedChapter===c.id?accent:undefined, color:selectedChapter===c.id?accent:undefined, flexShrink:0 }}>Ch {c.number}</button>))}
          </div>
        </div>
      );
    };

    return (
      <div style={S.page} className="screen-enter">
        <style>{FONTS}</style>
        {authModal}
        <Confetti active={showConfetti} />
        <Nav backLabel={isMobile?'← Back':`← ${book.title}`} backFn={back} accentColor={accent} />
        <div style={{ maxWidth: mode==='summary'?740:680, margin:'0 auto', padding: isMobile?'32px 16px 80px':'52px 32px 100px' }}>
          <div className="fade-up" style={{ display:'flex', alignItems:'center', gap:14, marginBottom:52 }}>
            <div style={{ fontSize:32 }}>{book.emoji}</div>
            <div>
              <div style={{ ...S.serif, fontSize: isMobile?20:24, fontWeight:600, lineHeight:1.1 }}>{book.title}</div>
              <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginTop:4 }}>{{summary:'DEEP SUMMARY',flash:'FLASHCARDS',quiz:'QUIZ MODE'}[mode]}</div>
            </div>
          </div>

          {mode === 'summary' && (
            chapters.length === 0 && !contentLoading ? (
              <div style={{ color:'#444', textAlign:'center', padding:64 }}>
                <div style={{ fontSize:32, marginBottom:16 }}>📚</div>
                <div style={{ fontSize:15, marginBottom:8 }}>Summary coming soon.</div>
                <div style={{ fontSize:13, color:'#555' }}>We're still adding content for this book — check back soon!</div>
              </div>
            ) : (
              <SummaryModule book={book} chapters={chapters} takeaways={takeaways} accent={accent} isMobile={isMobile} userProgress={userProgress} saveProgress={saveProgress}
                guestLimited={!user && book.id !== FREE_BOOK}
                onSignUp={() => openAuth('signup', `Enjoying ${book.title}? Create a free account to unlock all chapters.`)}
                onComplete={() => {
                setShowConfetti(true);
                setTimeout(() => { setShowConfetti(false); setScreen('book'); }, 3000);
              }} />
            )
          )}

          {mode === 'flash' && (
            <div>
              <ChapterFilter />
              {flashLoading ? (
                <div style={{ color:'#555', textAlign:'center', padding:64, fontSize:14 }}>
                  <div style={{ fontSize:32, marginBottom:16 }}>⏳</div>
                  Loading flashcards...
                </div>
              ) : filteredFlash.length===0 ? (
                <div style={{ color:'#444', textAlign:'center', padding:64 }}>
                  <div style={{ fontSize:32, marginBottom:16 }}>📚</div>
                  <div style={{ fontSize:15, marginBottom:8 }}>No flashcards available yet.</div>
                  <div style={{ fontSize:13, color:'#555' }}>Check back soon — we're adding more content!</div>
                </div>
              ) : flashSessionDone ? (
                <div className="pop-in" style={{ textAlign:'center', padding: isMobile?'40px 20px':'56px 40px' }}>
                  <div style={{ fontSize:64, marginBottom:20 }}>{flashMissed.length===0?'🏆':flashMissed.length<=3?'💪':'📚'}</div>
                  <div style={{ ...S.serif, fontSize: isMobile?32:44, fontWeight:300, marginBottom:12 }}>
                    {flashReviewMode ? 'Review Complete!' : 'Deck Complete!'}
                  </div>
                  {!flashReviewMode && (
                    <div style={{ marginBottom:28 }}>
                      <div style={{ fontSize: isMobile?44:58, color:accent, fontWeight:700, ...S.serif, lineHeight:1 }}>
                        {filteredFlash.length - flashMissed.length}
                        <span style={{ fontSize:'0.45em', color:'#333' }}>/{filteredFlash.length}</span>
                      </div>
                      <div style={{ fontSize:13, color:'#555', marginTop:8 }}>cards you knew</div>
                    </div>
                  )}
                  <div style={{ color:'#444', marginBottom:36, fontSize:15 }}>
                    {flashMissed.length===0 ? 'Perfect round — you knew every card!'
                      : flashReviewMode ? 'Nice work getting through the review pass.'
                      : `${flashMissed.length} card${flashMissed.length>1?'s':''} to review — want another pass?`}
                  </div>
                  <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
                    {!flashReviewMode && flashMissed.length > 0 && (
                      <button style={S.btn(accent)} onClick={startFlashReview}>Review {flashMissed.length} Missed →</button>
                    )}
                    {(() => {
                      // Show Next Chapter if user was on a specific chapter and there's a next one
                      if (selectedChapter !== 'all') {
                        const currentChIdx = chapters.findIndex(c => c.id === selectedChapter);
                        const nextCh = chapters[currentChIdx + 1];
                        if (nextCh) {
                          return (
                            <button style={S.btn(accent)} onClick={() => {
                              resetFlashDeck();
                              setSelectedChapter(nextCh.id);
                              setFlashMissed([]); setFlashReviewMode(false); setFlashSessionDone(false);
                              setFlashIndex(0); setFlipped(false);
                            }}>Ch {nextCh.number} Flashcards →</button>
                          );
                        }
                      }
                      return null;
                    })()}
                    <button style={S.ghost} onClick={resetFlashDeck}>Start Over</button>
                    <button style={S.ghost} onClick={() => setScreen('book')}>Back to Book</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontSize:12, color:'#555' }}>
                      {flashReviewMode ? `Review · Card ${flashIndex+1} of ${flashMissed.length}` : `Card ${flashIndex+1} of ${filteredFlash.length}`}
                    </span>
                    <span style={{ fontSize:11, color:'#555' }}>
                      {(() => {
                        const deck = flashReviewMode ? filteredFlash.filter((_,i) => flashMissed.includes(i)) : filteredFlash;
                        return deck[flashIndex]?.chapters ? `Ch ${deck[flashIndex].chapters.number}` : '';
                      })()}
                    </span>
                  </div>
                  {!flashReviewMode && flashMissed.length > 0 && (
                    <div style={{ fontSize:11, color:'#C0392B', marginBottom:8, textAlign:'right' }}>{flashMissed.length} to review</div>
                  )}
                  <div className="progress-track" style={{ marginBottom:36 }}>
                    <div className="progress-fill" style={{ width: flashReviewMode ? `${((flashIndex+1)/Math.max(flashMissed.length,1))*100}%` : `${((flashIndex+1)/filteredFlash.length)*100}%`, background: accent }} />
                  </div>
                  {(() => {
                    const deck = flashReviewMode ? filteredFlash.filter((_,i) => flashMissed.includes(i)) : filteredFlash;
                    const card = deck[flashIndex];
                    return (
                      <div style={{ perspective:'1200px', marginBottom: flipped?16:28 }}>
                        <div className={`flash-card-inner${flipped?' flipped':''}`} onClick={handleFlip}>
                          <div className="flash-face" style={{ background:`linear-gradient(135deg,#0f0f0f,${bookBg}55)`, border:`1.5px solid ${accent}22` }}>
                            <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginBottom:24 }}>TERM</div>
                            <div style={{ ...S.serif, fontSize: isMobile?26:34, fontWeight:600, lineHeight:1.2, marginBottom:24, color:'#F0EDE6' }}>{card?.term}</div>
                            <div style={{ fontSize:11, color:'#555', letterSpacing:1 }}>tap to reveal →</div>
                          </div>
                          <div className="flash-face flash-back" style={{ background:`linear-gradient(135deg,${bookBg}66,#0f0f0f)`, border:`1.5px solid ${accent}44` }}>
                            <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:3, marginBottom:24 }}>DEFINITION</div>
                            <div style={{ color:'#c8c8c8', fontSize: isMobile?15:17, lineHeight:1.8, maxWidth:420 }}>{card?.definition}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {flipped ? (
                    <div className="fade-up" style={{ display:'flex', gap:10, marginBottom:28 }}>
                      <button style={{ flex:1, background:'#0d2418', border:'1.5px solid #27AE6044', borderRadius:14, padding:'14px 10px', color:'#4ade80', fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:14, cursor:'pointer', transition:'all .15s' }} onClick={handleFlashKnewIt}>✓ Knew it</button>
                      <button style={{ flex:1, background:'#2a0d0d', border:'1.5px solid #C0392B44', borderRadius:14, padding:'14px 10px', color:'#f87171', fontFamily:"'Outfit',sans-serif", fontWeight:700, fontSize:14, cursor:'pointer', transition:'all .15s' }} onClick={handleFlashMissed}>✗ Review again</button>
                    </div>
                  ) : (
                    <div style={{ height:60, marginBottom:28 }} />
                  )}
                  <div className="dot-nav" style={{ marginBottom:20 }}>
                    {(() => {
                      const deck = flashReviewMode ? filteredFlash.filter((_,i) => flashMissed.includes(i)) : filteredFlash;
                      const total = deck.length;
                      const maxDots = 12;
                      if (total <= maxDots) {
                        return deck.map((_,i) => (
                          <div key={i} className={`dot${i===flashIndex?' active':''}`} style={{ background:i===flashIndex?accent:i<flashIndex?'#27AE60':'#1e1e1e' }} />
                        ));
                      }
                      const half = Math.floor(maxDots/2);
                      let start = Math.max(0, flashIndex-half);
                      let end = Math.min(total-1, start+maxDots-1);
                      if (end-start < maxDots-1) start = Math.max(0, end-maxDots+1);
                      return Array.from({ length: end-start+1 }, (_,k) => start+k).map(i => (
                        <div key={i} className={`dot${i===flashIndex?' active':''}`} style={{ background:i===flashIndex?accent:i<flashIndex?'#27AE60':'#1e1e1e' }} />
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === 'quiz' && (
            <div>
              <ChapterFilter />
              {quizLoading ? (
                <div style={{ color:'#555', textAlign:'center', padding:64, fontSize:14 }}>
                  <div style={{ fontSize:32, marginBottom:16 }}>⏳</div>
                  Loading questions...
                </div>
              ) : filteredQuiz.length===0 ? (
                <div style={{ color:'#444', textAlign:'center', padding:64 }}>
                  <div style={{ fontSize:32, marginBottom:16 }}>📚</div>
                  <div style={{ fontSize:15, marginBottom:8 }}>No questions available yet.</div>
                  <div style={{ fontSize:13, color:'#555' }}>Check back soon — we're adding more content!</div>
                </div>
              ) : quizDone ? (
                <div className="pop-in" style={{ textAlign:'center', padding: isMobile?'40px 20px':'56px 40px' }}>
                  <div style={{ fontSize:72, marginBottom:20 }}>{score/filteredQuiz.length>=0.8?'🏆':score/filteredQuiz.length>=0.5?'👍':'📚'}</div>
                  <div style={{ ...S.serif, fontSize: isMobile?36:48, fontWeight:300, marginBottom:8 }}>Quiz Complete</div>
                  <div style={{ fontSize: isMobile?52:68, color:accent, fontWeight:700, ...S.serif, marginBottom:8, lineHeight:1 }}>{score}<span style={{ fontSize:'0.5em', color:'#333' }}>/{filteredQuiz.length}</span></div>
                  <div style={{ fontSize:14, color:score/filteredQuiz.length>=0.8?'#4ade80':score/filteredQuiz.length>=0.5?'#C8B96E':'#C0392B', fontWeight:600, marginBottom:8, letterSpacing:1 }}>{Math.round((score/filteredQuiz.length)*100)}% CORRECT</div>
                  {(() => {
                    const prog = userProgress.find(p => p.book_id === book.id);
                    const best = prog?.quiz_score || 0;
                    return best > 0 && (
                      <div style={{ fontSize:12, color:'#555', marginBottom:8 }}>
                        {score >= best
                          ? <span style={{ color:'#4ade80', fontWeight:700 }}>🎉 New best score!</span>
                          : <span>Best: {best}/{filteredQuiz.length} · {Math.round((best/filteredQuiz.length)*100)}%</span>}
                      </div>
                    );
                  })()}
                  <div style={{ color:'#444', marginBottom:40, fontSize:15 }}>{score/filteredQuiz.length>=0.8?'Excellent retention! You really know this material.':score/filteredQuiz.length>=0.5?'Solid effort — review the ones you missed.':'Keep studying — active recall gets easier with practice.'}</div>
                  <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
                    <button style={S.btn(accent)} onClick={() => {setQuizIndex(0);setSelected(null);setShowExp(false);setScore(0);setQuizDone(false);}}>Retake Quiz</button>
                    <button style={S.ghost} onClick={() => setScreen('book')}>Back to Book</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <span style={{ fontSize:12, color:'#555' }}>Question {quizIndex+1} / {filteredQuiz.length}</span>
                    <span style={{ fontSize:13, color:accent, fontWeight:700 }}>{score} correct</span>
                  </div>
                  <div className="progress-track" style={{ marginBottom:36 }}>
                    <div className="progress-fill" style={{ width:`${(quizIndex/filteredQuiz.length)*100}%`, background:accent }} />
                  </div>
                  {currentQ?.chapters && <div style={{ fontSize:10, color:'#555', letterSpacing:2, marginBottom:20, fontWeight:600 }}>CHAPTER {currentQ.chapters.number} · {currentQ.chapters.title?.toUpperCase()}</div>}
                  <div className="fade-in" key={quizIndex} style={{ background:'#0a0a0a', border:'1px solid #1a1a1a', borderRadius:20, padding: isMobile?'24px 20px':'32px 36px', marginBottom:24 }}>
                    <div style={{ ...S.serif, fontSize: isMobile?20:24, fontWeight:600, lineHeight:1.45, color:'#F0EDE6' }}>{currentQ?.question}</div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:24 }}>
                    {currentQ?.options?.map((opt,i) => {
                      const isCorrect=i===currentQ.correct_index, isSelected=i===selected;
                      const cls=selected!==null?(isCorrect?'correct':isSelected?'wrong':''):'';
                      return (
                        <button key={i} className={`answer-btn${cls?' '+cls:''}`} onClick={() => handleAnswer(i)} disabled={selected!==null}>
                          <span style={{ minWidth:26, height:26, borderRadius:8, background:cls==='correct'?'#27AE6033':cls==='wrong'?'#C0392B33':'#1e1e1e', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0, color:cls==='correct'?'#4ade80':cls==='wrong'?'#f87171':'#555' }}>{String.fromCharCode(65+i)}</span>
                          {opt}
                          {cls==='correct'&&<span style={{ marginLeft:'auto' }}>✓</span>}
                          {cls==='wrong'&&<span style={{ marginLeft:'auto' }}>✗</span>}
                        </button>
                      );
                    })}
                  </div>
                  {showExp && (
                    <div className="fade-up" style={{ background:`${accent}08`, border:`1px solid ${accent}1a`, borderRadius:14, padding:'18px 22px', marginBottom:20 }}>
                      <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:2, marginBottom:8 }}>EXPLANATION</div>
                      <div style={{ color:'#666', fontSize:14, lineHeight:1.7 }}>{currentQ?.explanation}</div>
                    </div>
                  )}
                  {selected!==null && (
                    <button className="fade-up" style={{ ...S.btn(accent), width:'100%', padding:16, fontSize:15 }} onClick={nextQuestion}>
                      {quizIndex+1>=filteredQuiz.length?'See My Results →':'Next Question →'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {bookComplete && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20, backdropFilter:'blur(12px)' }}>
            <Confetti active={true} />
            <div className="pop-in" style={{ background:`linear-gradient(145deg,#0f0f0f,${bookBg}66)`, border:`1px solid ${accent}33`, borderRadius:28, padding: isMobile?'40px 28px':'60px 64px', maxWidth:520, width:'100%', textAlign:'center', position:'relative', zIndex:1 }}>
              <div style={{ fontSize:80, marginBottom:16 }}>🏆</div>
              <div style={{ fontSize:10, color:accent, fontWeight:700, letterSpacing:4, marginBottom:16 }}>BOOK COMPLETE</div>
              <h2 style={{ ...S.serif, fontSize: isMobile?32:46, fontWeight:600, lineHeight:1.1, marginBottom:12 }}>{book.title}</h2>
              <div style={{ fontSize:14, color:'#555', marginBottom:36 }}>You've completed all three learning modes.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:40 }}>
                {[
                  { icon:'📖', label:'Summary', value:'Done' },
                  { icon:'🃏', label:'Flashcards', value:`${filteredFlash.length || '—'} cards` },
                  { icon:'✅', label:'Quiz Best', value: (() => { const p = userProgress.find(x => x.book_id === book.id); return p?.quiz_total ? `${p.quiz_score}/${p.quiz_total}` : '—'; })() },
                ].map(({ icon, label, value }) => (
                  <div key={label} style={{ background:'#0a0a0a', border:`1px solid ${accent}18`, borderRadius:16, padding:'16px 10px' }}>
                    <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
                    <div style={{ fontSize:10, color:'#555', fontWeight:600, letterSpacing:1, marginBottom:4 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize:13, color:accent, fontWeight:700 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <button style={{ ...S.btn(accent), padding:'14px 32px', fontSize:15 }} onClick={() => { setBookComplete(false); setScreen('library'); }}>Explore More Books →</button>
                <button style={{ ...S.ghost, padding:'12px 32px', fontSize:14 }} onClick={() => setBookComplete(false)}>Stay Here</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div style={S.page}><style>{FONTS}</style></div>;
}